// Centralized latency bounds for Algolia calls (SPR-2766).
// During an Algolia incident, unbounded server-side calls pile up slow renders and
// exhaust resources. These bounds make every call fail fast and degrade gracefully.

// Per-attempt SDK timeouts (milliseconds). read is lowered from the 5s v5 default so an
// unresponsive host fails over to the next retry host sooner.
export const ALGOLIA_CLIENT_TIMEOUTS: { connect: number; read: number; write: number } = {
  connect: 2000,
  read: 3000,
  write: 30000,
};

// Overall deadline (ms) for a whole search call, including the SDK's multi-host retries.
export const ALGOLIA_SEARCH_DEADLINE_MS = 5000;

// Timeout (ms) for the raw settings fetch, which uses fetch() rather than the SDK.
export const ALGOLIA_SETTINGS_TIMEOUT_MS = 3000;

/**
 * A deadline that rejects with `error` after `ms`, paired with a `cancel()` that clears
 * the underlying timer. Use with Promise.race to cap the total time of a call whose SDK
 * doesn't accept an AbortSignal, and call `cancel()` in a `.finally()` so the timer
 * doesn't outlive a call that wins the race.
 */
export function algoliaDeadline(ms: number, error: Error): { promise: Promise<never>; cancel: () => void } {
  let timer: ReturnType<typeof setTimeout>;
  const promise = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(error), ms);
  });
  return { promise, cancel: () => clearTimeout(timer) };
}
